import { useState, useEffect } from "react";
import { classNames } from "primereact/utils";
import { OnboardingItemKey } from "../../common/enums";
import { IOnboardingGroup, IOnboardingStep } from "./flows/onboardingTypes";
import { OnboardingStepItem } from "./OnboardingStepItem";
import styles from "./styles/onboarding.module.scss";

interface IOnboardingAccordionGroupProps {
  group: IOnboardingGroup;
  steps: IOnboardingStep[];
  pendingItemKeys: Set<OnboardingItemKey>;
  activeStepId: OnboardingItemKey | null;
  defaultExpanded: boolean;
  onStepClick: (stepId: OnboardingItemKey) => void;
}

export const OnboardingAccordionGroup = ({
  group,
  steps,
  pendingItemKeys,
  activeStepId,
  defaultExpanded,
  onStepClick,
}: IOnboardingAccordionGroupProps) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // Auto-expand when a step in this group becomes active
  useEffect(() => {
    if (activeStepId && steps.some((s) => s.id === activeStepId)) {
      setIsExpanded(true);
    }
  }, [activeStepId, steps]);

  const groupCompletedCount = steps.filter(
    (s) => !pendingItemKeys.has(s.id)
  ).length;
  const isGroupComplete = groupCompletedCount === steps.length;

  return (
    <div className={styles.accordionGroup}>
      <div
        className={styles.accordionGroupHeader}
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        <span className={styles.accordionGroupTitle}>
          {group.title}
          {isGroupComplete && " \u2713"}
        </span>
        <span
          className={classNames(styles.accordionGroupChevron, {
            [styles.accordionGroupChevronExpanded]: isExpanded,
          })}
        >
          ›
        </span>
      </div>
      <div
        className={classNames(styles.accordionGroupSteps, {
          [styles.accordionGroupStepsExpanded]: isExpanded,
        })}
      >
        <div className={styles.accordionGroupStepsInner}>
          {steps.map((step) => (
            <OnboardingStepItem
              key={step.id}
              step={step}
              isPending={pendingItemKeys.has(step.id)}
              isActive={activeStepId === step.id}
              onClick={() => onStepClick(step.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
